import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {noop} from "lodash";
import moment from "moment";
import React, {useEffect, useState} from "react";
import OtpInput from 'react-otp-input';
import {useTimer} from "react-timer-hook";
import {useTranslation} from "react-i18next";
import {color, font, type} from "common/tokens";
import CtaButton from "components/redesign/CtaButton";

const RESEND_AFTER = () => moment(new Date()).add(2, 'm').toDate();

const useStyles = makeStyles({
  head: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 16,
  },
  title: {
    ...type.eyebrow,
    color: color.TEXT_MUTED,
    flex: 1,
    margin: 0,
  },
  resend: {
    ...type.meta,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: color.ACCENT_TEXT,
    whiteSpace: 'nowrap',
  },
  counting: {
    color: color.TEXT_SUBTLE,
  },
  // The library renders `div > div(x6) > input`, with an inline `width: 1em` on
  // each input. The row and the per-input wrappers are laid out from here, and
  // the inline width is overridden so the boxes share the card evenly.
  boxes: {
    marginBottom: 22,
    '& > div': {
      display: 'flex',
      gap: 8,
      width: '100%',
    },
    '& input': {
      ...type.body,
      flex: '1 1 0',
      minWidth: 0,
      fontSize: 20,
      fontWeight: font.SEMIBOLD,
      width: '100% !important',
      height: 52,
      padding: 0,
      boxSizing: 'border-box',
      color: color.TEXT,
      background: color.SURFACE,
      border: `1px solid ${color.BORDER}`,
      borderRadius: 12,
      textAlign: 'center',
      '&:focus': {
        outline: 'none',
        borderColor: color.ACCENT,
      },
    },
  },
  boxesErrored: {
    '& input': {
      borderColor: color.DANGER,
    },
  },
  error: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.DANGER,
    textAlign: 'center',
    margin: '14px 0 0',
  },
  message: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    textAlign: 'center',
    margin: '14px 0 0',
  },
});

export default function OTP({
  error,
  onAnswered = noop,
  message = "",
  currentStep,
  resendOtp,
  otpErrorMessage = '',
  numInputs = 6,
  isInputNum = false,
  loading = false
}) {
  const classes = useStyles();
  const {t} = useTranslation();

  const [otp, setOtp] = useState("");
  const [counting, setCounting] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const {seconds, minutes, restart} = useTimer({
    expiryTimestamp: RESEND_AFTER(),
    onExpire: () => setCounting(false),
  });

  useEffect(() => {
    const next = RESEND_AFTER();
    return () => restart(next);
  }, [currentStep]);

  const handleResend = () => {
    if (counting) return;
    resendOtp();
    restart(RESEND_AFTER());
    setCounting(true);
  };

  const errored = submitted && Boolean(error);

  const submit = async event => {
    if (event) event.preventDefault();
    await onAnswered(otp);
    setSubmitted(true);
  };

  return (
    <form onSubmit={submit}>
      <div className={classes.head}>
        <p className={classes.title}>{t('enterOTP')}</p>
        <button
          type={'button'}
          disabled={counting || loading}
          onClick={handleResend}
          className={`${classes.resend} ${counting ? classes.counting : ''}`}
        >
          {counting ? `${t('resendOTP')} (${minutes}:${String(seconds).padStart(2, '0')})` : t('resendOTP')}
        </button>
      </div>

      <div
        role={'group'}
        aria-label={t('enterOTP')}
        className={classNames(classes.boxes, errored && classes.boxesErrored)}
      >
        <OtpInput
          numInputs={numInputs}
          value={otp}
          onChange={setOtp}
          inputType={isInputNum ? 'number' : 'text'}
          shouldAutoFocus
          renderInput={props => <input {...props}/>}
        />
      </div>

      <CtaButton
        type={'submit'}
        size={'large'}
        fullWidth
        label={t('continueCaps')}
        disabled={otp.toString().length < numInputs}
        loading={loading}
      />

      {errored && <p className={classes.error} role={'alert'}>{otpErrorMessage}</p>}
      {Boolean(message) && <p className={classes.message}>{message}</p>}
    </form>
  );
};
